import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactQuill from 'react-quill';
import { Box } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { debounce } from '../../utils/debounce';

import EditorToolbar, { formats, getModules } from './Toolbar';

import 'react-quill/dist/quill.snow.css';
import './styles.scss';

/**
 * RichTextEditor
 *
 * Controlled component that renders a react quill text editor. You need to pass it the value and the onChange
 * handler in order for it to have any function.
 *
 * @param id - unique identifier for editor to avoid ID selector collisions
 * @param label
 * @param value
 * @param showToolbar
 * @param onChange
 * @param innerRef
 * @returns {JSX.Element}
 * @constructor
 */
const RichTextEditor = ({ id, value, showToolbar, toolBarPosition, onChange, innerRef, children }) => {
  const [innerValue, setInnerValue] = useState(value);

  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  const onChangeDebounced = useMemo(() => debounce(onChange), [onChange]);

  const handleChange = useCallback(val => {
    setInnerValue(val);
    onChangeDebounced(val);
  }, [onChangeDebounced]);

  return (
    <Box className="RichTextEditor">
      {(showToolbar && toolBarPosition === 'top') && <EditorToolbar id={id} />}
      <ReactQuill
        formats={formats}
        modules={showToolbar ? getModules(id) : { toolbar: false }}
        ref={innerRef}
        style={{ minHeight: 0 }}
        theme="snow"
        value={innerValue}
        onChange={handleChange}
      />
      {(showToolbar && toolBarPosition === 'bottom') && <EditorToolbar id={id} />}
      <div className="children">
        {children}
      </div>
    </Box>
  );
};

RichTextEditor.propTypes = {
  id: PropTypes.string.isRequired,
  showToolbar: PropTypes.bool,
  toolBarPosition: PropTypes.oneOf(['top', 'bottom']),
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  children: PropTypes.element,
  innerRef: PropTypes.object.isRequired // eslint-disable-line react/forbid-prop-types
};

RichTextEditor.defaultProps = { showToolbar: false, toolBarPosition: 'top' };

export default RichTextEditor;
